import {
  Icon3dCubeSphere,
  IconCash,
  IconFiles,
  IconGps,
  IconId,
  IconLicense,
  IconReceipt2,
  IconReportSearch,
  IconTags,
  IconUsers,
} from '@tabler/icons-react'
import {
  IconBuildingFactory2,
  IconDashboard,
  IconDeviceDesktopCog,
  IconDevicesBolt,
  IconUsersGroup,
  IconWriting,
} from '@tabler/icons-react'
import type { MenuLink, SubLink } from '@testsa-com/common/src/types/Menu.type'
import type { TFunction } from 'i18next'
import type { NextRouter } from 'next/router'

export const ICON_PROPS = {
  size: 14,
  stroke: 1.5,
  style: {
    marginRight: '0.5rem',
  },
}

export const MenuHelper = {
  getLinks: (t: TFunction<'common'>): MenuLink[] => {
    return [
      {
        label: t('words.administration'),
        icon: <IconDeviceDesktopCog {...ICON_PROPS} />,
        IconElement: IconDeviceDesktopCog,
        subLinks: [
          { type: 'menuLabel', label: t('words.project_other') },
          {
            type: 'link',
            link: '/projects/',
            label: t('projects.listOfProjects', { ns: 'pages' }),
            icon: <IconFiles {...ICON_PROPS} />,
            IconElement: IconFiles,
          },
          {
            type: 'link',
            link: '/projects-resources/',
            label: t('projects.projectsResources', { ns: 'pages' }),
            icon: <Icon3dCubeSphere {...ICON_PROPS} />,
            IconElement: Icon3dCubeSphere,
            permission: 'projects.list',
          },
          { type: 'divider' },
          {
            type: 'link',
            link: '/pollsters/',
            label: t('words.pollster_other'),
            icon: <IconReportSearch {...ICON_PROPS} />,
            IconElement: IconReportSearch,
            permission: 'pollsters.list',
          },
          {
            type: 'link',
            link: '/equipments/',
            label: t('words.equipment_one'),
            icon: <IconDevicesBolt {...ICON_PROPS} />,
            IconElement: IconDevicesBolt,
            permission: 'equipments.list',
          },
          { type: 'divider' },
          {
            type: 'link',
            link: '/users/',
            label: t('words.user_other'),
            icon: <IconUsers {...ICON_PROPS} />,
            IconElement: IconUsers,
            permission: 'users.list',
          },
          {
            type: 'link',
            link: '/roles/',
            label: t('words.role_other'),
            icon: <IconId {...ICON_PROPS} />,
            IconElement: IconId,
            permission: 'roles.list',
          },
        ],
      },
      {
        label: t('words.client_other'),
        icon: <IconBuildingFactory2 {...ICON_PROPS} />,
        IconElement: IconBuildingFactory2,
        permission: 'clients.organizations.view',
        subLinks: [
          {
            type: 'link',
            link: '/dashboards/',
            label: t('words.dashboard_other'),
            icon: <IconDashboard {...ICON_PROPS} />,
            IconElement: IconDashboard,
            permission: 'clients.organizations.view',
          },
        ],
      },
      {
        label: t('words.hr'),
        icon: <IconUsersGroup {...ICON_PROPS} />,
        IconElement: IconUsersGroup,
        isComingSoon: process.env.NEXT_PUBLIC_FEAT_HR !== 'true',
        permission: ['hr.contracts.view', 'pollsters.admin_data'],
        subLinks: [
          { type: 'menuLabel', label: t('words.payroll_other') },
          {
            type: 'link',
            link: '/hr/payroll/payouts',
            label: t('hr.payout_other', { ns: 'pages' }),
            icon: <IconReceipt2 {...ICON_PROPS} />,
            IconElement: IconReceipt2,
            permission: 'payroll.payouts.view',
          },
          {
            type: 'link',
            link: '/hr/payroll/payables',
            label: t('hr.payable_other', { ns: 'pages' }),
            icon: <IconCash {...ICON_PROPS} />,
            IconElement: IconCash,
            permission: 'payroll.payables.view',
          },
          {
            type: 'link',
            link: '/hr/payroll/task-rates',
            label: t('hr.rate_other', { ns: 'pages' }),
            icon: <IconTags {...ICON_PROPS} />,
            IconElement: IconTags,
            permission: 'payroll.rates.view',
          },
          { type: 'divider' },
          { type: 'menuLabel', label: t('words.pollster_other') },
          {
            type: 'link',
            link: '/hr/contracts',
            label: t('words.contract_other'),
            icon: <IconWriting {...ICON_PROPS} />,
            IconElement: IconWriting,
            permission: 'hr.contracts.view',
          },
          {
            type: 'link',
            link: '/hr/pollsters-documents/',
            label: t('hr.pollstersDocuments', { ns: 'pages' }),
            icon: <IconLicense {...ICON_PROPS} />,
            IconElement: IconLicense,
            permission: 'pollsters.admin_data',
          },
        ],
      },
      {
        link: '/field/',
        label: t('fieldOffice.title'),
        icon: <IconGps {...ICON_PROPS} />,
        IconElement: IconGps,
        permission: 'field.access',
      },
    ]
  },
  getActiveLink: (links: MenuLink[], pathname: NextRouter['pathname']): MenuLink | undefined => {
    if (pathname === '/') {
      return undefined
    }

    return links.find(l => {
      if (l.subLinks) {
        return !!l.subLinks.find(
          sl =>
            pathname === (sl as SubLink).link ||
            pathname.startsWith(`/${(sl as SubLink).link?.replaceAll('/', '')}`),
        )
      }

      return l.link && l.link !== '/' && pathname.startsWith(`/${l.link?.replaceAll('/', '')}`)
    })
  },
  ICON_PROPS,
}
